<template>
    <!-- Main Panel (all states except multi-panel AUDIT mode) -->
    <SidePanel
        :visible="
            !!(auditStore && auditStore?.isPanelVisible) && !useMultiPanelMode
        "
        :title="auditStore?.panelTitle"
        :size="
            (auditStore?.audit?.audit_type === 'ASSESSMENT' ||
                performingAudit) &&
            isTemplatePanel(auditStore?.panelType)
                ? 'panel-lg'
                : 'panel-md'
        "
        :confirm-close="false"
        @close="handlePanelCloseAttempt"
    >
        <template #title-badge>
            <span
                v-if="
                    isInProgressPerformPanel &&
                    draftSaveStatus.status !== 'idle'
                "
                class="d-flex align-items-center gap-1 small ms-2"
                :class="draftSaveStatus.className"
                aria-live="polite"
            >
                <span
                    v-if="draftSaveStatus.showSpinner"
                    class="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                ></span>
                <span v-else>{{ draftSaveStatus.icon }}</span>
                <span>{{ draftSaveStatus.label }}</span>
            </span>
        </template>
        <template class="d-flex flex-column h-100">
            <AuditSummary
                ref="auditSummary"
                v-if="isSummaryPanel(auditStore?.panelType)"
                :panel-type="auditStore?.panelType"
                :audit="auditStore?.audit"
                @open-modal="openModal(auditStore?.audit)"
                @areas="
                    openOptionPanel(
                        $t('audits.panels.options.area'),
                        'audit-area'
                    )
                "
                @edit-audit="
                    changePanel(
                        isAuditScheduled(auditStore?.audit)
                            ? 'schedule'
                            : 'editSummary',
                        auditStore?.audit
                    )
                "
                @view-audit="changePanel('viewTemplate', auditStore?.audit)"
                @perform-audit="
                    changePanel('performTemplate', auditStore?.audit)
                "
                @continue-audit="
                    changePanel('performTemplate', auditStore?.audit)
                "
                @submit="handleAuditForm"
            />
            <AuditTemplateContent
                ref="auditTemplateContent"
                v-else-if="isTemplatePanel(auditStore?.panelType)"
                :audit="auditStore?.audit"
                :can-edit="auditStore?.panelType !== 'viewTemplate'"
                :user-options="userOptions"
                @areas="
                    openOptionPanel(
                        $t('audits.panels.options.area'),
                        'audit-area'
                    )
                "
                @started="handleMainPanelStarted"
                @panel-phase-change="handlePerformTemplatePhaseChange"
                @draft-save-status-change="handleDraftSaveStatusChange"
                @draft-saved="handleDraftSaved"
                @submit="handleAuditSubmit"
                @completed="handleAuditSubmit"
                @action-point-conflict="handleAuditConflict"
            />
            <ScheduleAuditForm
                v-else-if="auditStore?.panelType == 'schedule'"
                :audit="auditStore?.audit || {}"
                @areas="
                    openOptionPanel(
                        $t('audits.panels.options.area'),
                        'audit-area'
                    )
                "
                @submit="handleAuditSubmit"
            />
            <OptionPanel
                :visible="isOptionPanelVisible"
                :option-title="optionTitle"
                :endpoint="endpoint"
                @submit="handleOptionFormSubmit"
                @close="closeOptionPanel"
            />
        </template>
        <template #footer>
            <div
                v-if="
                    showBackButton ||
                    showSubmitButton ||
                    showSaveDraftButton ||
                    showEditButton
                "
                class="d-flex gap-2 pt-4 border-top mt-4"
            >
                <!-- Back Button -->
                <button
                    v-if="showBackButton"
                    type="button"
                    class="btn btn-white"
                    @click="handleBack"
                >
                    <div class="d-flex gap-2">
                        <i class="fi fi-chevron-left"></i>
                        {{ $t('audits.panels.actions.back') }}
                    </div>
                </button>
                <!-- Save Draft Button -->
                <div v-if="showSaveDraftButton" class="flex-grow-1">
                    <ToolTip full-width>
                        <template #tooltip-trigger>
                            <button
                                type="button"
                                class="btn btn-white w-100"
                                :disabled="draftButtonMeta.disabled"
                                @click="saveDraftToComponent"
                            >
                                <div
                                    class="d-flex align-items-center justify-content-center gap-2"
                                >
                                    <span
                                        v-if="draftButtonMeta.showSpinner"
                                        class="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    {{ draftButtonMeta.label }}
                                </div>
                            </button>
                        </template>
                        <template v-if="draftButtonMeta.title" #tooltip-content>
                            {{ draftButtonMeta.title }}
                        </template>
                    </ToolTip>
                </div>
                <!-- Submit Button -->
                <button
                    v-if="showSubmitButton"
                    type="submit"
                    class="btn btn-secondary flex-grow-1"
                    @click="submitToComponent"
                >
                    {{ $t('audits.panels.actions.submit') }}
                </button>
                <!-- Edit Button -->
                <IconButton
                    v-if="showEditButton"
                    wrapper-class="ms-auto"
                    preset="edit"
                    @click="handleEditAuditTemplate"
                />
            </div>
        </template>
    </SidePanel>

    <!-- PerformTemplate Panels Container -->
    <div
        ref="panelsContainer"
        class="panels-container"
        :class="{
            'has-visible-panels': useMultiPanelMode,
        }"
        @scroll="updateScrollState"
    >
        <TransitionGroup
            name="panel-slide"
            tag="div"
            class="panels-transition-group"
        >
            <div
                v-for="(panel, index) in panelInstances"
                :key="panel.id"
                class="panel-wrapper"
            >
                <SidePanel
                    :visible="
                        useMultiPanelMode &&
                        (index === 0
                            ? !!(auditStore && auditStore?.isPanelVisible)
                            : true)
                    "
                    :hide-overlay="index > 0"
                    :inert-background="false"
                    :title="auditStore?.panelTitle"
                    size="panel-md"
                    :confirm-close="() => shouldConfirmPanelClose(panel.id)"
                    :panel-style="getPanelStyle(index)"
                    @close="(source) => closePanelInstance(panel.id, source)"
                >
                    <template #title-badge>
                        <span
                            v-if="
                                getPanelDraftStatus(panel.id).status !== 'idle'
                            "
                            class="d-flex align-items-center gap-1 small ms-2"
                            :class="getPanelDraftStatus(panel.id).className"
                            aria-live="polite"
                        >
                            <span
                                v-if="getPanelDraftStatus(panel.id).showSpinner"
                                class="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                            ></span>
                            <span v-else>{{
                                getPanelDraftStatus(panel.id).icon
                            }}</span>
                            <span>{{
                                getPanelDraftStatus(panel.id).label
                            }}</span>
                        </span>
                    </template>
                    <template class="d-flex flex-column h-100">
                        <AuditTemplateContent
                            :ref="getPanelRef(panel.id)"
                            :audit="panel.auditData || null"
                            :can-edit="true"
                            :user-options="userOptions"
                            :panel-id="panel.id"
                            :start-in-perform-mode="true"
                            @areas="
                                openOptionPanel(
                                    $t('audits.panels.options.area'),
                                    'audit-area'
                                )
                            "
                            @started="
                                (auditType) =>
                                    handlePanelStarted(panel.id, auditType)
                            "
                            @draft-save-status-change="
                                (s) => handlePanelDraftStatus(panel.id, s)
                            "
                            @draft-saved="handleDraftSaved"
                            @submit="handlePanelSubmit(panel.id)"
                            @action-point-conflict="
                                handlePanelAuditConflict(panel.id, $event)
                            "
                        />
                    </template>
                    <template #footer>
                        <div
                            v-if="performingAudit"
                            class="d-flex gap-2 pt-4 border-top mt-4"
                        >
                            <!-- Save Draft Button -->
                            <div class="flex-grow-1">
                                <ToolTip full-width>
                                    <template #tooltip-trigger>
                                        <button
                                            type="button"
                                            class="btn btn-white w-100"
                                            :disabled="
                                                getPanelDraftButtonMeta(
                                                    panel.id
                                                ).disabled
                                            "
                                            @click="saveDraftForPanel(panel.id)"
                                        >
                                            <div
                                                class="d-flex align-items-center justify-content-center gap-2"
                                            >
                                                <span
                                                    v-if="
                                                        getPanelDraftButtonMeta(
                                                            panel.id
                                                        ).showSpinner
                                                    "
                                                    class="spinner-border spinner-border-sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                {{
                                                    getPanelDraftButtonMeta(
                                                        panel.id
                                                    ).label
                                                }}
                                            </div>
                                        </button>
                                    </template>
                                    <template
                                        v-if="
                                            getPanelDraftButtonMeta(panel.id)
                                                .title
                                        "
                                        #tooltip-content
                                    >
                                        {{
                                            getPanelDraftButtonMeta(panel.id)
                                                .title
                                        }}
                                    </template>
                                </ToolTip>
                            </div>
                            <!-- Submit Button -->
                            <button
                                type="submit"
                                class="btn btn-secondary flex-grow-1"
                                @click="submitPanelInstance(panel.id)"
                            >
                                {{ $t('audits.panels.actions.submit') }}
                            </button>
                        </div>
                    </template>
                </SidePanel>
            </div>
        </TransitionGroup>
    </div>

    <!-- Fixed Footer Banner for multi-panel AUDIT mode -->
    <div v-if="useMultiPanelMode" class="fixed-footer-banner">
        <div class="footer-banner-content">
            <!-- Back Button -->
            <div class="footer-banner-left">
                <button
                    v-if="showBackInFooter"
                    type="button"
                    class="btn btn-white"
                    @click="handleBackForFirstPanel"
                >
                    <div class="d-flex">
                        <i
                            v-if="!$isMobile"
                            class="fi fi-chevron-left me-2"
                        ></i>
                        {{ $t('audits.panels.actions.back') }}
                        <span v-if="!$isMobile" class="ms-1">{{
                            performBackLabel
                        }}</span>
                    </div>
                </button>
            </div>
            <!-- Scroll Navigation -->
            <div
                v-if="canScrollLeft || canScrollRight"
                class="footer-banner-center"
            >
                <button
                    type="button"
                    class="icon-btn icon-btn-lg icon-btn-white"
                    :disabled="!canScrollLeft"
                    @click="scrollLeft"
                    :title="$t('common.iconButtons.collapse')"
                >
                    <i class="fi fi-chevron-left"></i>
                </button>
                <button
                    type="button"
                    class="icon-btn icon-btn-lg icon-btn-white"
                    :disabled="!canScrollRight"
                    @click="scrollRight"
                    :title="$t('common.iconButtons.expand')"
                >
                    <i class="fi fi-chevron-right"></i>
                </button>
            </div>
            <!-- Add Another Button -->
            <div class="footer-banner-right">
                <button class="btn btn-white" @click="submitAllPanels">
                    {{ $t('audits.panels.actions.submitAll') }}
                </button>
                <button
                    type="button"
                    class="btn btn-light"
                    :class="$isMobile ? 'icon-btn icon-btn-lg' : ''"
                    :disabled="!canAddAnotherPanel"
                    @click="addAnotherPanel"
                >
                    <div class="d-flex gap-2">
                        <span v-if="!$isMobile">{{
                            $t('audits.panels.actions.addAnotherAudit')
                        }}</span>
                        <i class="fi fi-add"></i>
                    </div>
                </button>
            </div>
        </div>
    </div>

    <ConfirmModal
        :visible="isCloseDraftModalVisible"
        title="Save your progress?"
        confirm-button-text="Save draft and exit"
        @close="handleKeepEditing"
        @confirm="handleSaveDraftAndExit"
    >
        <p class="my-2" style="color: var(--neutral-800)">
            You have unsaved changes. Save a draft before exiting, or discard
            changes and revert to your last saved draft.
        </p>
        <div class="d-flex flex-column gap-2 w-100 mt-3">
            <button
                type="button"
                class="btn btn-white w-100"
                @click="handleDiscardChangesAndExit"
            >
                Discard changes
            </button>
        </div>
    </ConfirmModal>

    <ConfirmModal
        :visible="isModalVisible"
        :title="$t('audits.panels.deleteAuditModal.title')"
        :confirm-button-text="$t('audits.panels.deleteAuditModal.confirm')"
        @close="closeModal"
        @confirm="handleDeleteAudit(auditStore?.audit)"
    >
        {{ $t('audits.panels.deleteAuditModal.description') }}
    </ConfirmModal>

    <ActionPointSiteConflictModal
        :visible="isActionPointConflictModalVisible"
        :old-site-name="actionPointConflictOldSiteName"
        :new-site-name="actionPointConflictNewSiteName"
        :affected-count="actionPointConflictAffectedCount"
        :conflicts="displayActionPointConflicts"
        :confirming="isConfirmingActionPointConflict"
        @close="closeActionPointConflictModal"
        @confirm="confirmActionPointConflict"
    />
</template>

<script>
import ConfirmModal from '../ConfirmModal.vue';
import SidePanel from '../SidePanel.vue';
import ActionPointSiteConflictModal from '@/components/ActionPointSiteConflictModal.vue';
import AuditSummary from './AuditSummary.vue';
import AuditTemplateContent from './AuditTemplateContent.vue';
import ScheduleAuditForm from './ScheduleAuditForm.vue';
import OptionPanel from './OptionPanel.vue';
import IconButton from '@/components/IconButton.vue';
import ToolTip from '@/components/fields/ToolTip.vue';

import { useAuditStore } from '@/stores/audits';
import { saveAudit, deleteAudit } from '@/utils/auditOperations';
import { getUserOptions } from '@/utils/userOptions';
import {
    isAuditComplete,
    isAuditDraft,
    isAuditScheduled,
} from '@/utils/auditStatus';
import {
    isSummaryPanel,
    isTemplatePanel,
    getBackPanelType,
} from '@/utils/auditPanelStrategies';
import {
    AUDIT_DRAFT_SAVE_STATUS,
    getAuditDraftButtonMeta,
} from './utils/auditDraftAutosave';
import {
    cloneConflictPayload,
    getClearedAccountablesCount,
    isActionPointAccountableConflict,
} from '@/utils/actionPointAccountableConflict.js';

const JUST_SAVED_DRAFT_DISPLAY_MS = 2000;

export default {
    name: 'AuditsPanel',
    components: {
        SidePanel,
        AuditSummary,
        AuditTemplateContent,
        OptionPanel,
        ScheduleAuditForm,
        ConfirmModal,
        ActionPointSiteConflictModal,
        IconButton,
        ToolTip,
    },
    props: {
        visible: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            auditStore: null,
            performingAudit: false,
            isModalVisible: false,
            actionPointConflictResponse: null,
            pendingActionPointConflictPayload: null,
            pendingActionPointConflictPanelId: null,
            isConfirmingActionPointConflict: false,
            userOptions: [],
            // Option panel state
            isOptionPanelVisible: false,
            optionTitle: '',
            endpoint: '',
            // All performTemplate panels tracked in array (including first panel)
            // Always keep first panel instance rendered so transitions work properly
            // Additional panels (index > 0) are always performing audits
            panelInstances: [{ id: 1, auditData: null }],
            nextPanelId: 2,
            // Store the audit template data to clone for new panels
            sharedAuditData: null,
            // Scroll state
            canScrollLeft: false,
            canScrollRight: false,
            isCloseDraftModalVisible: false,
            performTemplatePhase: null,
            draftSaveStatus: {
                status: 'idle',
                label: '',
                className: 'text-muted',
                showSpinner: false,
                hasUnsavedChanges: false,
            },
            justSavedDraft: false,
            justSavedDraftTimer: null,
            panelDraftStatuses: {},
            panelJustSavedDraft: {},
            panelJustSavedTimers: {},
            hasDraftSaves: false,
        };
    },
    computed: {
        draftButtonMeta() {
            return getAuditDraftButtonMeta(this.draftSaveStatus.status, {
                justSaved: this.justSavedDraft,
                hasUnsavedChanges: this.draftSaveStatus.hasUnsavedChanges,
            });
        },
        isInProgressPerformPanel() {
            if (!this.isOnPerformForm) {
                return false;
            }
            if (this.performingAudit) {
                return true;
            }
            const audit = this.auditStore?.audit;
            if (!audit?.id) {
                return false;
            }
            return isAuditDraft(audit) || isAuditScheduled(audit);
        },
        isOnPerformForm() {
            if (this.auditStore?.panelType !== 'performTemplate') {
                return false;
            }
            return (
                this.performTemplatePhase != null &&
                this.performTemplatePhase !== 'select'
            );
        },
        isAdminRoute() {
            return this.$route.matched.some((r) => r.name === 'Admin');
        },
        // Multi-panel mode: only when performTemplate + AUDIT type + performing
        useMultiPanelMode() {
            return (
                this.auditStore?.panelType === 'performTemplate' &&
                this.performingAudit &&
                this.sharedAuditData?.audit_type === 'AUDIT'
            );
        },
        // Scheduled audits can open performTemplate directly in form mode
        // (without emitting started), so treat this as main-panel performing state.
        isScheduledPerformMainPanel() {
            return (
                this.auditStore?.panelType === 'performTemplate' &&
                !!this.auditStore?.audit?.id &&
                !!this.auditStore?.audit?.template_id &&
                !this.auditStore?.audit?.form_json &&
                !this.performingAudit
            );
        },
        isActionPointConflictModalVisible() {
            return !!this.actionPointConflictResponse;
        },
        displayActionPointConflicts() {
            const conflicts = this.actionPointConflictResponse?.conflicts;
            return Array.isArray(conflicts) ? conflicts : [];
        },
        actionPointConflictAffectedCount() {
            return (
                Number(this.actionPointConflictResponse?.affected_count) || 0
            );
        },
        actionPointConflictOldSiteName() {
            return (
                this.auditStore?.audit?.site_name ||
                this.auditStore?.audit?.site?.name ||
                'the current site'
            );
        },
        actionPointConflictNewSiteName() {
            const siteId = this.pendingActionPointConflictPayload?.site_id;
            const site = this.$siteStore?.getSiteById(siteId);
            return site?.name || 'the new site';
        },
        // Non-AUDIT performTemplate in performing state (uses main panel with footer buttons)
        isNonAuditPerforming() {
            return (
                this.auditStore?.panelType === 'performTemplate' &&
                this.performingAudit &&
                !this.useMultiPanelMode
            );
        },
        // Footer button visibility computed properties
        showBackButton() {
            if (this.auditStore?.panelType === 'uploadSummary') {
                return false;
            }
            return (
                this.isNonAuditPerforming ||
                this.auditStore?.panelType === 'viewTemplate' ||
                this.auditStore?.panelType === 'editTemplate' ||
                this.auditStore?.panelType === 'editSummary'
            );
        },
        showSubmitButton() {
            return (
                this.isInProgressPerformPanel ||
                this.auditStore?.panelType === 'editTemplate' ||
                this.auditStore?.panelType === 'editSummary' ||
                this.auditStore?.panelType === 'uploadSummary'
            );
        },
        showSaveDraftButton() {
            return this.isInProgressPerformPanel;
        },
        showEditButton() {
            return (
                this.auditStore?.panelType === 'viewTemplate' &&
                this.$userStore.hasPerm('audits:write') &&
                (!this.auditStore.audit.app_admin_only || this.isAdminRoute)
            );
        },
        // Check if we can add another panel (max 3 total)
        canAddAnotherPanel() {
            return (
                this.panelInstances.length < 4 &&
                this.sharedAuditData?.audit_type === 'AUDIT'
            );
        },
        // Check if any panel is performing an audit
        hasPerformingAudit() {
            return this.performingAudit;
        },
        // Show back button in footer if performing audit
        showBackInFooter() {
            return this.performingAudit;
        },
        performBackLabel() {
            const audit = this.auditStore?.audit;
            if (audit?.id && (isAuditDraft(audit) || isAuditScheduled(audit))) {
                return this.$t('audits.panels.actions.backToSummary');
            }
            return this.$t('audits.panels.actions.backToSelection');
        },
    },
    methods: {
        // Expose strategy functions to template
        isSummaryPanel,
        isTemplatePanel,
        getBackPanelType,
        isAuditComplete,
        isAuditDraft,
        isAuditScheduled,

        changePanel(type, audit) {
            return this.openPanelWithAudit(type, audit);
        },
        async openPanelWithAudit(type, audit) {
            let resolvedAudit = audit;

            if (
                type === 'performTemplate' &&
                resolvedAudit?.id &&
                isAuditDraft(resolvedAudit)
            ) {
                const fresh = await this.fetchAuditRecord(resolvedAudit);
                if (fresh) {
                    resolvedAudit = fresh;
                }
            }

            this.auditStore.openPanel(type, resolvedAudit);
            if (type == 'performTemplate') {
                this.performTemplatePhase = null;
                // Reset first panel when starting performTemplate
                if (this.panelInstances.length > 0) {
                    this.panelInstances[0] = {
                        id: this.panelInstances[0].id, // Keep same ID
                        auditData: resolvedAudit || null,
                    };
                }
                // Remove any additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                // Reset shared audit data when starting fresh
                this.sharedAuditData = null;
                const isResume =
                    resolvedAudit?.id &&
                    resolvedAudit?.template_id &&
                    isAuditDraft(resolvedAudit);
                this.performingAudit = Boolean(isResume);
            } else {
                this.performTemplatePhase = null;
                // Reset panels when switching away from performTemplate
                if (this.panelInstances.length > 0) {
                    this.panelInstances[0].auditData = null;
                }
                // Remove any additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                this.performingAudit = false;
                this.sharedAuditData = null;
                this.resetDraftSaveStatus();
            }
        },
        resolveAuditorLabel(audit) {
            if (!audit?.auditor_id) {
                return null;
            }

            const id = audit.auditor_id;
            const option = (this.userOptions || []).find(
                (o) => o.value === id || String(o.value) === String(id)
            );
            if (option?.text) {
                return option.text;
            }
            if (option?.label) {
                return option.label;
            }

            const employees = this.$employeesStore?.employees || [];
            const consultants = this.$employeesStore?.consultants || [];
            const person = [...employees, ...consultants].find(
                (p) => p.id === id || String(p.id) === String(id)
            );
            if (person?.first_name && person?.last_name) {
                return `${person.first_name} ${person.last_name}`;
            }

            return null;
        },
        mergeAuditRecordForPanel(existing, incoming) {
            if (!incoming) {
                return existing;
            }

            const base = existing ? { ...existing } : {};
            const record = { ...base, ...incoming };

            record.site_name =
                incoming.site?.name ||
                incoming.site_name ||
                base.site_name ||
                null;
            record.area_name =
                incoming.area?.name ||
                incoming.area_name ||
                base.area_name ||
                null;
            record.auditor =
                (typeof base.auditor === 'string' && base.auditor) ||
                (typeof incoming.auditor === 'string' && incoming.auditor) ||
                this.resolveAuditorLabel(incoming) ||
                this.resolveAuditorLabel(base) ||
                null;

            if (!record.reviewer_name && base.reviewer_name) {
                record.reviewer_name = base.reviewer_name;
            }
            if (!record.reviewer && base.reviewer) {
                record.reviewer = base.reviewer;
            }

            return record;
        },
        async fetchAuditRecord(audit) {
            if (!audit?.id && !audit?.hash) {
                return null;
            }

            const payload = {
                fields: [
                    'id',
                    'hash',
                    'name',
                    'date',
                    'status',
                    'completed_at',
                    'form_json',
                    'template_id',
                    'audit_type',
                    'app_admin_only',
                    'auditor_id',
                    'site_id',
                    'area_id',
                    'details',
                    'form_score',
                    'form_max_score',
                    'score_override',
                    'published_to_client',
                ],
                include: {
                    files: true,
                    site: {
                        fields: ['id', 'name'],
                    },
                    area: {
                        fields: ['id', 'name'],
                    },
                },
                where: audit.id ? { id: audit.id } : { hash: audit.hash },
            };

            const response = await this.$getData('audit/read', payload);
            if (response?.success && response.data?.length > 0) {
                return this.mergeAuditRecordForPanel(audit, response.data[0]);
            }

            return null;
        },
        defaultDraftSaveStatus() {
            return {
                status: 'idle',
                label: '',
                className: 'text-muted',
                showSpinner: false,
                hasUnsavedChanges: false,
            };
        },
        handleDraftSaveStatusChange(status) {
            this.draftSaveStatus = status || this.defaultDraftSaveStatus();
        },
        clearJustSavedDraftTimer() {
            if (this.justSavedDraftTimer) {
                clearTimeout(this.justSavedDraftTimer);
                this.justSavedDraftTimer = null;
            }
        },
        resetDraftSaveStatus() {
            this.draftSaveStatus = this.defaultDraftSaveStatus();
            this.clearJustSavedDraftTimer();
            this.justSavedDraft = false;
        },
        getPanelDraftStatus(panelId) {
            return (
                this.panelDraftStatuses[panelId] ||
                this.defaultDraftSaveStatus()
            );
        },
        getPanelDraftButtonMeta(panelId) {
            const status = this.getPanelDraftStatus(panelId);
            return getAuditDraftButtonMeta(status.status, {
                justSaved: Boolean(this.panelJustSavedDraft[panelId]),
                hasUnsavedChanges: status.hasUnsavedChanges,
            });
        },
        clearPanelJustSavedTimer(panelId) {
            if (this.panelJustSavedTimers[panelId]) {
                clearTimeout(this.panelJustSavedTimers[panelId]);
                delete this.panelJustSavedTimers[panelId];
            }
        },
        handlePanelDraftStatus(panelId, status) {
            const resolved = status || this.defaultDraftSaveStatus();
            const previous = this.panelDraftStatuses[panelId];
            this.panelDraftStatuses[panelId] = resolved;

            if (resolved.status === 'saved' && previous?.status !== 'saved') {
                this.clearPanelJustSavedTimer(panelId);
                this.panelJustSavedDraft[panelId] = true;
                this.panelJustSavedTimers[panelId] = setTimeout(() => {
                    this.panelJustSavedDraft[panelId] = false;
                    delete this.panelJustSavedTimers[panelId];
                }, JUST_SAVED_DRAFT_DISPLAY_MS);
            } else if (resolved.status !== 'saved') {
                this.clearPanelJustSavedTimer(panelId);
                this.panelJustSavedDraft[panelId] = false;
            }
        },
        clearPanelDraftState(panelId) {
            delete this.panelDraftStatuses[panelId];
            delete this.panelJustSavedDraft[panelId];
            this.clearPanelJustSavedTimer(panelId);
        },
        getPanelComponent(panelId) {
            const refs = this.$refs[this.getPanelRef(panelId)];
            return Array.isArray(refs) ? refs[0] : refs;
        },
        shouldConfirmPanelClose(panelId) {
            const component = this.getPanelComponent(panelId);
            const status = this.getPanelDraftStatus(panelId);
            const hasUnsavedChanges =
                component?.hasUnsavedChanges?.() ?? status.hasUnsavedChanges;
            const draftSaveStatus = component?.draftSaveStatus || status.status;

            return Boolean(
                hasUnsavedChanges &&
                    draftSaveStatus === AUDIT_DRAFT_SAVE_STATUS.ERROR
            );
        },
        saveDraftForPanel(panelId) {
            return this.getPanelComponent(panelId)?.saveDraft?.();
        },
        flushPanelDraft(panelId) {
            return this.getPanelComponent(panelId)?.flushPendingDraft?.();
        },
        flushAllPanelDrafts() {
            return Promise.all(
                this.panelInstances.map((panel) =>
                    this.flushPanelDraft(panel.id)
                )
            );
        },
        handlePerformTemplatePhaseChange(phase) {
            this.performTemplatePhase = phase;
        },
        handleMainPanelStarted(auditType) {
            // Handle started event from main panel AuditTemplateContent
            // Capture full audit data from the main panel component
            const component = this.$refs.auditTemplateContent;
            if (component?.localAudit) {
                this.sharedAuditData = {
                    id: component.auditId,
                    template_id: component.localAudit.template_id,
                    audit_type: component.localAudit.audit_type,
                    date: component.localAudit.date,
                    auditor_id: component.localAudit.auditor_id,
                    site_id: component.localAudit.site_id,
                    area_id: component.localAudit.area_id,
                    name: component.localAudit.name,
                    description: component.localAudit.description,
                    company_id: component.localAudit.company_id,
                    form_max_score: component.localAudit.form_max_score,
                };
                // If AUDIT type, pass data to first panel instance so it doesn't restart
                if (auditType === 'AUDIT' && this.panelInstances.length > 0) {
                    this.panelInstances[0].auditData = {
                        ...this.sharedAuditData,
                    };
                }
            } else {
                this.sharedAuditData = { audit_type: auditType };
            }
            this.performingAudit = true;
        },
        handlePanelStarted(panelId, auditType) {
            // Set performing state and audit type
            this.performingAudit = true;
            if (auditType) {
                this.sharedAuditData = {
                    ...this.sharedAuditData,
                    audit_type: auditType,
                };
            }
            // Capture audit data from the rightmost panel (last in array)
            const index = this.panelInstances.findIndex(
                (p) => p.id === panelId
            );
            if (index === this.panelInstances.length - 1) {
                this.capturePanelAuditData(panelId);
            }
        },
        capturePanelAuditData(panelId) {
            // Capture audit data from a panel instance
            const refKey = this.getPanelRef(panelId);
            const refs = this.$refs[refKey];
            if (refs) {
                const component = Array.isArray(refs) ? refs[0] : refs;
                if (
                    component &&
                    component.localAudit &&
                    component.panelType !== 'select'
                ) {
                    // Capture the audit metadata (template, date, auditor, site, area)
                    const auditData = {
                        template_id: component.localAudit.template_id,
                        audit_type: component.localAudit.audit_type,
                        date: component.localAudit.date,
                        auditor_id: component.localAudit.auditor_id,
                        site_id: component.localAudit.site_id,
                        area_id: component.localAudit.area_id,
                        name: component.localAudit.name,
                        description: component.localAudit.description,
                        company_id: component.localAudit.company_id,
                        form_max_score: component.localAudit.form_max_score,
                    };

                    // Store in shared data for cloning to new panels
                    this.sharedAuditData = auditData;
                }
            }
        },
        openOptionPanel(type, endpoint) {
            this.optionTitle = type;
            this.endpoint = endpoint;
            this.isOptionPanelVisible = true;
        },
        closeOptionPanel() {
            this.isOptionPanelVisible = false;
            this.optionTitle = '';
            this.endpoint = '';
        },
        closePanel({ settle = true } = {}) {
            this.finalizeClosePanel({ settle });
        },
        async handlePanelCloseAttempt() {
            const component = this.$refs.auditTemplateContent;

            if (this.isOnPerformForm && component?.hasUnsavedChanges?.()) {
                const canAutoFlush =
                    component.draftSaveStatus !== AUDIT_DRAFT_SAVE_STATUS.ERROR;
                if (!canAutoFlush) {
                    this.isCloseDraftModalVisible = true;
                    return;
                }
            }

            await this.finalizeClosePanel();
        },
        handleKeepEditing() {
            this.isCloseDraftModalVisible = false;
        },
        async handleSaveDraftAndExit() {
            const component = this.$refs.auditTemplateContent;
            const savedAudit = await component?.saveDraft?.();
            if (!savedAudit) {
                return;
            }

            this.isCloseDraftModalVisible = false;
            this.finalizeClosePanel();
        },
        handleDiscardChangesAndExit() {
            this.isCloseDraftModalVisible = false;
            this.finalizeClosePanel({ flush: false });
        },
        async finalizeClosePanel({ flush = true, settle = true } = {}) {
            this.closeActionPointConflictModal();
            if (flush) {
                const flushed = await this.flushMainPanelDraft();
                if (flushed === false) {
                    this.isCloseDraftModalVisible = true;
                    return false;
                }
            }
            if (settle) {
                this.settleDraftSessionOnExit();
            } else {
                this.hasDraftSaves = false;
            }
            this.auditStore.closePanel();
            this.performingAudit = false;
            this.performTemplatePhase = null;
            // Reset first panel but keep it rendered
            if (this.panelInstances.length > 0) {
                this.panelInstances[0].auditData = null;
            }
            // Remove any additional panels
            if (this.panelInstances.length > 1) {
                this.panelInstances.splice(1);
            }
            this.sharedAuditData = null;
            this.resetDraftSaveStatus();
            return true;
        },
        handleDraftSaved(savedAudit, meta = {}) {
            const storeAuditId = this.auditStore?.audit?.id;
            if (
                savedAudit &&
                (!storeAuditId || savedAudit.id === storeAuditId)
            ) {
                this.auditStore.$patch((state) => {
                    state.audit = this.mergeAuditRecordForPanel(
                        state.audit,
                        savedAudit
                    );
                });
            }
            this.hasDraftSaves = true;
            if (meta?.source === 'manual') {
                this.auditStore.incrementAuditScheduleVersionKey();
                this.auditStore.incrementAuditHistoryVersionKey();
            }
        },
        flushMainPanelDraft() {
            return this.$refs.auditTemplateContent?.flushPendingDraft?.();
        },
        settleDraftSessionOnExit() {
            if (this.hasDraftSaves) {
                this.auditStore.incrementAuditScheduleVersionKey();
                this.auditStore.incrementAuditHistoryVersionKey();
                this.$alertStore.pushSuccess(
                    'Draft saved. You can continue it anytime from Drafts.'
                );
            }
            this.hasDraftSaves = false;
        },
        async saveDraftToComponent() {
            await this.$refs.auditTemplateContent?.saveDraft?.();
        },
        openModal() {
            this.isModalVisible = true;
        },
        closeModal() {
            this.isModalVisible = false;
        },
        closeActionPointConflictModal() {
            this.actionPointConflictResponse = null;
            this.pendingActionPointConflictPayload = null;
            this.pendingActionPointConflictPanelId = null;
            this.isConfirmingActionPointConflict = false;
        },
        async confirmActionPointConflict() {
            if (!this.pendingActionPointConflictPayload) return;

            this.isConfirmingActionPointConflict = true;
            const payload = {
                ...cloneConflictPayload(this.pendingActionPointConflictPayload),
                acknowledge_clear_accountables: true,
            };

            try {
                const response = await this.$postData('audit/update', payload);
                if (response?.success) {
                    const clearedCount = getClearedAccountablesCount(response);
                    const newSiteName = this.actionPointConflictNewSiteName;
                    const panelId = this.pendingActionPointConflictPanelId;
                    this.closeActionPointConflictModal();
                    if (panelId != null) {
                        this.handlePanelSubmit(panelId);
                    } else {
                        this.handleAuditSubmit();
                    }
                    this.$alertStore.pushSuccess(
                        this.$t(
                            'audits.panels.success.siteUpdatedClearedAssignments',
                            {
                                count: clearedCount,
                                site: newSiteName,
                            }
                        )
                    );
                } else if (isActionPointAccountableConflict(response)) {
                    this.actionPointConflictResponse = response;
                } else {
                    this.$alertStore.pushError(
                        response?.message ||
                            this.$t('audits.panels.errors.saveAudit')
                    );
                }
            } catch (error) {
                this.$alertStore.pushError(
                    this.$t('audits.panels.errors.saveAudit')
                );
            } finally {
                this.isConfirmingActionPointConflict = false;
            }
        },
        submitToComponent() {
            if (this.isInProgressPerformPanel) {
                if (this.$refs.auditTemplateContent?.completeAudit) {
                    this.$refs.auditTemplateContent.completeAudit();
                } else if (this.$refs.auditTemplateContent?.saveAudit) {
                    this.$refs.auditTemplateContent.saveAudit();
                }
            } else if (this.auditStore?.panelType === 'editTemplate') {
                this.submitCreateAudit();
            } else {
                this.submitAuditForm();
            }
        },
        submitAllPanels() {
            this.panelInstances.forEach((panel) => {
                this.submitPanelInstance(panel.id);
            });
        },
        submitCreateAudit() {
            // Trigger save on child component via ref (only for editTemplate)
            if (this.$refs.auditTemplateContent) {
                this.$refs.auditTemplateContent.saveAudit();
            }
        },
        async handleBack() {
            if (this.isNonAuditPerforming) {
                const audit = this.auditStore?.audit;
                if (
                    audit?.id &&
                    (isAuditDraft(audit) || isAuditScheduled(audit))
                ) {
                    await this.flushMainPanelDraft();
                    this.settleDraftSessionOnExit();
                    this.performingAudit = false;
                    this.sharedAuditData = null;
                    const fresh = await this.fetchAuditRecord(audit);
                    this.auditStore.openPanel('viewSummary', fresh || audit);
                    return;
                }
                if (this.$refs.auditTemplateContent?.backToSelection) {
                    this.$refs.auditTemplateContent.backToSelection();
                }
                this.performingAudit = false;
                this.sharedAuditData = null;
            } else {
                const backType = getBackPanelType(this.auditStore?.panelType);
                if (backType !== null) {
                    this.auditStore.openPanel(backType, this.auditStore?.audit);
                }
            }
        },
        submitAuditForm() {
            // Trigger submit on child component via ref
            if (this.$refs.auditSummary) {
                this.$refs.auditSummary.submitForm();
            }
        },
        async getUserList() {
            this.userOptions = await getUserOptions(this);
        },
        handleOptionFormSubmit() {
            this.closeOptionPanel();
        },
        handleApiError(areaResponse, siteResponse) {
            this.$alertStore.pushError(
                areaResponse?.message || siteResponse?.message
            );
        },
        setActionPointConflict({ payload, response, panelId = null }) {
            this.pendingActionPointConflictPayload =
                cloneConflictPayload(payload);
            this.pendingActionPointConflictPanelId = panelId;
            this.actionPointConflictResponse = response;
        },
        handleAuditConflict({ payload, response }) {
            this.setActionPointConflict({ payload, response });
        },
        handlePanelAuditConflict(panelId, { payload, response }) {
            this.setActionPointConflict({ payload, response, panelId });
        },
        handleAuditForm(audit, newEntry, sendNotification = false) {
            // Use single audit endpoint for all audit types
            const action = newEntry ? 'create' : 'update';
            const endpoint = `audit/${action}`;

            this.$postData(endpoint, audit).then((response) => {
                if (response?.success) {
                    const auditId = response.data?.id || audit?.id;

                    // Send notification if checkbox was checked (only for new entries)
                    if (sendNotification && newEntry && auditId) {
                        this.$postData('audit/notify-upload', {
                            audit_id: auditId,
                        })
                            .then((notifyResponse) => {
                                if (!notifyResponse?.success) {
                                    this.$alertStore.pushError(
                                        notifyResponse?.message ||
                                            this.$t(
                                                'audits.panels.errors.sendNotification'
                                            )
                                    );
                                }
                            })
                            .catch(() => {
                                this.$alertStore.pushError(
                                    this.$t(
                                        'audits.panels.errors.sendNotification'
                                    )
                                );
                            });
                    }

                    this.handleAuditSubmit();
                } else if (
                    !newEntry &&
                    isActionPointAccountableConflict(response)
                ) {
                    this.setActionPointConflict({ payload: audit, response });
                } else {
                    this.$alertStore.pushError(response?.message);
                }
            });
        },
        handleAuditSubmit() {
            this.auditStore.incrementAuditHistoryVersionKey();
            this.auditStore.incrementAuditScheduleVersionKey();
            this.closePanel({ settle: false });
        },
        handleDeleteAudit(audit) {
            deleteAudit(this, audit).then((response) => {
                if (response?.success) {
                    this.handleAuditSubmit();
                    this.isModalVisible = false;
                } else {
                    this.$alertStore.pushError(response?.message);
                }
            });
        },
        handleEditAuditTemplate() {
            if (!this.$userStore.companyHasPerm('audits:write')) {
                this.$userStore.togglePremiumModal(true);
                return;
            }
            this.auditStore.openPanel('editTemplate', this.auditStore?.audit);
        },
        // Multiple panel methods
        addAnotherPanel() {
            // Always capture the latest audit data from the rightmost panel (last in array)
            const rightmostPanel =
                this.panelInstances[this.panelInstances.length - 1];
            if (rightmostPanel) {
                this.capturePanelAuditData(rightmostPanel.id);
            }

            // Create new panel with cloned audit data
            // Additional panels (index > 0) are always performing audits
            const newPanel = {
                id: this.nextPanelId++,
                auditData: this.sharedAuditData
                    ? { ...this.sharedAuditData }
                    : null,
            };
            this.panelInstances.push(newPanel);
            // Update scroll state after adding panel
            this.$nextTick(() => {
                this.updateScrollState();
            });
        },
        async closePanelInstance(panelId, closeSource) {
            // If in performTemplate mode and performing audit, and close is from overlay,
            // close all panels when confirming
            if (
                this.useMultiPanelMode &&
                this.performingAudit &&
                closeSource === 'overlay'
            ) {
                await this.closeAllPanelsAndReset();
                this.$nextTick(() => {
                    this.updateScrollState();
                });
                return;
            }

            const isExit = closeSource !== 'submit';

            if (isExit) {
                const flushed = await this.flushPanelDraft(panelId);
                if (flushed === false) {
                    return;
                }
            }

            const index = this.panelInstances.findIndex(
                (p) => p.id === panelId
            );
            if (index !== -1) {
                this.clearPanelDraftState(panelId);
                if (this.panelInstances.length === 1) {
                    this.closePanel({ settle: isExit });
                } else {
                    if (isExit) {
                        this.settleDraftSessionOnExit();
                    } else {
                        this.hasDraftSaves = false;
                    }
                    // Additional panel - remove it
                    this.panelInstances.splice(index, 1);
                    // Update scroll state after panel removal
                    this.$nextTick(() => {
                        this.updateScrollState();
                    });
                }
            }
        },
        async closeAllPanelsAndReset() {
            // Close all panels and reset performTemplate state
            const flushed = await this.flushAllPanelDrafts();
            if (flushed.some((result) => result === false)) {
                return;
            }
            this.settleDraftSessionOnExit();
            this.auditStore.closePanel();
            this.performingAudit = false;
            // Reset all panel instances
            this.panelInstances.forEach((panel) =>
                this.clearPanelDraftState(panel.id)
            );
            this.panelInstances = [{ id: 1, auditData: null }];
            this.nextPanelId = 2;
            this.sharedAuditData = null;
        },
        getPanelRef(panelId) {
            return `auditTemplateContent_${panelId}`;
        },
        submitPanelInstance(panelId) {
            const refKey = this.getPanelRef(panelId);
            const refs = this.$refs[refKey];
            if (refs) {
                const component = Array.isArray(refs) ? refs[0] : refs;
                if (component && component.saveAudit) {
                    component.saveAudit();
                }
            }
        },
        handlePanelSubmit(panelId) {
            // Handle submit for any panel in performTemplate mode
            this.auditStore.incrementAuditHistoryVersionKey();
            this.auditStore.incrementAuditScheduleVersionKey();
            // Close the specific panel that was submitted
            // Remaining panels will automatically shift left due to array re-render
            this.closePanelInstance(panelId, 'submit');
            // Update scroll state after submit (closePanelInstance will also update, but this ensures it)
            this.$nextTick(() => {
                this.updateScrollState();
            });
        },
        isLastPanel(index) {
            // Check if this panel is the rightmost (last in array)
            return index === this.panelInstances.length - 1;
        },
        async handleBackForFirstPanel() {
            await this.flushAllPanelDrafts();
            this.settleDraftSessionOnExit();
            const audit = this.auditStore?.audit;
            if (audit?.id && (isAuditDraft(audit) || isAuditScheduled(audit))) {
                this.performingAudit = false;
                this.sharedAuditData = null;
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                const fresh = await this.fetchAuditRecord(audit);
                this.auditStore.openPanel('viewSummary', fresh || audit);
                return;
            }

            if (this.panelInstances.length > 0) {
                const refKey = this.getPanelRef(this.panelInstances[0].id);
                const refs = this.$refs[refKey];
                if (refs) {
                    const component = Array.isArray(refs) ? refs[0] : refs;
                    if (component && component.backToSelection) {
                        component.backToSelection();
                    }
                }
                // Remove all additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                // Reset performing state
                this.performingAudit = false;
            }
        },
        getPanelStyle(index) {
            // Set z-index for proper stacking
            return {
                zIndex: 1050 + index,
            };
        },
        updateScrollState() {
            const container = this.$refs.panelsContainer;

            // Early returns for invalid states
            if (!container) {
                this.canScrollLeft = false;
                this.canScrollRight = false;
                return;
            }

            // Only update scroll state when in performTemplate mode
            if (!this.useMultiPanelMode) {
                this.canScrollLeft = false;
                this.canScrollRight = false;
                return;
            }

            // Need at least 2 panels to scroll
            if (this.panelInstances.length < 2) {
                this.canScrollLeft = false;
                this.canScrollRight = false;
                return;
            }

            const { scrollLeft, scrollWidth, clientWidth } = container;

            // Validate dimensions - if content hasn't rendered yet, wait
            if (!scrollWidth || !clientWidth || scrollWidth <= clientWidth) {
                this.canScrollLeft = false;
                this.canScrollRight = false;
                return;
            }

            const maxScroll = Math.max(0, scrollWidth - clientWidth);
            const tolerance = 2;

            // Check if we can scroll in either direction
            // Account for small rounding and subpixel rendering differences
            this.canScrollLeft = scrollLeft > tolerance;
            this.canScrollRight = scrollLeft < maxScroll - tolerance;
        },
        scrollLeft() {
            const container = this.$refs.panelsContainer;
            if (!container) return;
            // Get the width of one panel wrapper to scroll by
            const transitionGroup = container.querySelector(
                '.panels-transition-group'
            );
            if (!transitionGroup) return;
            const firstPanel = transitionGroup.querySelector('.panel-wrapper');
            if (!firstPanel) return;
            const scrollAmount =
                firstPanel.offsetWidth +
                    parseFloat(getComputedStyle(transitionGroup).gap) || 525;
            container.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth',
            });
        },
        scrollRight() {
            const container = this.$refs.panelsContainer;
            if (!container) return;
            // Get the width of one panel wrapper to scroll by
            const transitionGroup = container.querySelector(
                '.panels-transition-group'
            );
            if (!transitionGroup) return;
            const firstPanel = transitionGroup.querySelector('.panel-wrapper');
            if (!firstPanel) return;
            const scrollAmount =
                firstPanel.offsetWidth +
                    parseFloat(getComputedStyle(transitionGroup).gap) || 525;
            container.scrollBy({
                left: scrollAmount,
                behavior: 'smooth',
            });
        },
    },
    watch: {
        'draftSaveStatus.status'(newStatus, oldStatus) {
            if (newStatus === 'saved' && oldStatus !== 'saved') {
                this.clearJustSavedDraftTimer();
                this.justSavedDraft = true;
                this.justSavedDraftTimer = setTimeout(() => {
                    this.justSavedDraft = false;
                    this.justSavedDraftTimer = null;
                }, JUST_SAVED_DRAFT_DISPLAY_MS);
            } else if (newStatus !== 'saved') {
                this.clearJustSavedDraftTimer();
                this.justSavedDraft = false;
            }
        },
        // Watch panel instances to update scroll state when panels change
        panelInstances: {
            handler() {
                this.$nextTick(() => {
                    this.updateScrollState();
                });
            },
            deep: true,
        },
        // Watch for panelType changes to reset performTemplate panels
        'auditStore.panelType'(newType) {
            if (newType === 'performTemplate') {
                // Reset first panel when switching to performTemplate
                if (this.panelInstances.length > 0) {
                    this.panelInstances[0].auditData = null;
                }
                // Remove any additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                this.sharedAuditData = null;
                const audit = this.auditStore?.audit;
                const isResume =
                    audit?.id && audit?.template_id && isAuditDraft(audit);
                this.performingAudit = Boolean(isResume);
                this.$nextTick(() => {
                    this.updateScrollState();
                });
            } else if (newType !== 'performTemplate') {
                this.performTemplatePhase = null;
                // Reset panels when switching away from performTemplate
                if (this.panelInstances.length > 0) {
                    this.panelInstances[0].auditData = null;
                }
                // Remove any additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                this.performingAudit = false;
                this.sharedAuditData = null;
                this.resetDraftSaveStatus();
            }
        },
    },
    created() {
        this.auditStore = useAuditStore();
    },
    mounted() {
        this.getUserList();
        // Update scroll state on mount and window resize
        this.$nextTick(() => {
            this.updateScrollState();
        });
        window.addEventListener('resize', this.updateScrollState);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateScrollState);
        this.clearJustSavedDraftTimer();
    },
};
</script>

<style scoped>
.fixed-footer-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(45, 45, 45, 0.5);
    backdrop-filter: blur(6px);
    padding: var(--space-2, 0.5rem) var(--space-5, 1.25rem);
    z-index: 1060;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.footer-banner-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 100%;
    margin: 0 auto;
}

.footer-banner-left,
.footer-banner-center,
.footer-banner-right {
    /* flex: 1; */
    display: flex;
    align-items: center;
    gap: var(--space-2, 0.5rem);
    width: fit-content;
    white-space: nowrap;
}

.footer-banner-left {
    justify-content: flex-start;
}

.footer-banner-center {
    justify-content: center;
    gap: var(--space-2, 0.5rem);
}

.footer-banner-center .icon-btn {
    min-width: auto;
    padding: var(--space-2, 0.5rem);
}

.footer-banner-right {
    justify-content: flex-end;
}

.panels-container {
    position: fixed;
    top: 1.25rem;
    right: 0;
    left: 0;
    bottom: 4rem;
    z-index: 1050;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    pointer-events: none; /* Don't block clicks when empty/hidden */
    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
}

.panels-container::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
}

.panels-container.has-visible-panels {
    pointer-events: auto; /* Enable clicks when panels are visible */
}

.panels-transition-group {
    display: flex;
    height: 100%;
    flex-direction: row-reverse;
    align-items: stretch;
    width: max-content;
    min-width: 100%;
    flex-shrink: 0;
}

.panel-wrapper {
    flex: 1 1 500px;
    min-width: 320px;
    max-width: calc(500px + 1.25rem);
    width: 100%;
    height: 100%;
    position: relative;
    scroll-snap-align: end;
    flex-shrink: 1;
    padding-right: 0.625rem;
    padding-left: 0.625rem;
}

/* Add margin to first and last panels */
.panel-wrapper:first-child {
    padding-right: 1.25rem;
    max-width: calc(500px + 1.875rem);
}

.panel-wrapper:last-child {
    padding-left: 1.25rem;
    max-width: calc(500px + 1.875rem);
}

.panel-wrapper:first-child:last-child {
    max-width: calc(500px + 2.5rem);
}

.panels-container :deep(.side-panel) {
    position: relative;
    width: 100%;
    height: 100%;
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    margin: 0;
}

.panels-container :deep(.side-panel.panel-md) {
    max-width: 100vw !important;
}

/* Panel slide animations */
.panel-slide-enter-active {
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.panel-slide-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
    overflow: hidden;
}

.panel-slide-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.panel-slide-leave-to {
    transform: translateX(100%);
    opacity: 0;
    flex: 0 0 0px !important;
    min-width: 0 !important;
    max-width: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
}

.panel-slide-move {
    transition: transform 0.3s ease;
}

@media only screen and (max-width: 499.98px) {
    .panels-container {
        top: var(--space-2);
        max-width: 100%;
    }

    .panels-transition-group {
        gap: var(--space-2);
    }

    .panel-wrapper {
        min-width: 0;
        max-width: 100vw;
        flex: 0 1 100%;
        width: 100vw;
        padding-right: 0.5rem !important;
        padding-left: 0.5rem !important;
    }

    .fixed-footer-banner {
        padding: var(--space-2, 0.5rem) var(--space-2, 0.5rem);
    }
}
</style>
